import * as amplitude from '@amplitude/analytics-browser';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Invoice from '@spike/invoice-model';
import { createInvoiceThunk, InvoicesStatus } from '@spike/invoices-action';
import { Order } from '@spike/order-model';
import {
    getOrderThunk,
    migrateBookingThunk,
    OrdersStatus,
    verifyOutdatedThunk,
    reloadOrderThunk
} from '@spike/orders-action';
import {
    confirmAndSendThunk,
    createPaymentLinkThunk,
    getEnrollmentStatusThunk,
    PaymentsStatus,
    sendThunk
} from '@spike/payments-action';
import { PaymentLink, PaymentMethodIds } from '@spike/payments-model';
import { isEmailValid } from '@spike/validations';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import { Button, SidePanel, Spinner } from 'components/UI';
import { AMPLITUDE } from 'constants/index';
import { useApiClientWrapper } from 'hooks';
import isEmpty from 'lodash/isEmpty';
import replace from 'lodash/replace';
import moment from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PayByLinkPath } from 'routes/Paths';
import { RootState } from 'store';
import CheckoutHeader from './CheckoutHeader';
import CheckoutOrder from './CheckoutOrder';
import CheckoutPayment, { PaymentInfo } from './CheckoutPayment';
import CreditCardPayment from './CreditCardPayment';
import DiscountModal from './DiscountModal';
import InvoiceResume from './InvoiceResume';
import NoPhoneAlert from './NoPhoneAlert';
import PayByLink from './PayByLink';
import PaymentDone from './PaymentDone';
import PosPayment from './PosPayment';
import RefreshModal from './UI/RefreshModal';
import Memberships from './Memberships';
import MembershipsButtons from './Memberships/MembershipsButtons';
import { checkOrderThunk, MembershipsStatus } from '@spike/memberships-action';
import { getAllOrderItems } from './utils/OrderUtils';

/* This component has the add general discount implemented, but it is cooment out because backend will implement it in a upcoming release. */

export type CheckoutType = 'singlesale' | 'membership' | 'booking';

export interface CheckoutProps {
    orderId?: number;
    bookingId?: number;
    type: CheckoutType;
    paymentMethods?: Array<PaymentMethodIds>;
    hideTips?: boolean;
    hide?: boolean;
    onClose: () => void;
    onBack?: () => void;
    onEditClient?: (clientId: number) => void;
    onPaid?: () => void;
    onPayByLinkSent?: () => void;
    onPayByLinkCreated?: () => void;
    onStartPayment?: () => void;
}
const enum CheckoutSteps {
    ORDER,
    ADD_MEMBERSHIPS,
    PAYMENT,
    DROP_IN,
    POS,
    PAY_BY_LINK,
    DONE
}

const showByStep = new Map([
    [
        CheckoutSteps.ORDER,
        {
            header: true,
            order: true,
            addMemberships: false,
            payment: false,
            invoiceResume: true,
            footer: true,
            dropIn: false,
            pos: false,
            done: false,
            payByLink: false
        }
    ],
    [
        CheckoutSteps.ADD_MEMBERSHIPS,
        {
            header: true,
            order: false,
            addMemberships: true,
            payment: false,
            invoiceResume: false,
            footer: false,
            dropIn: false,
            pos: false,
            done: false,
            payByLink: false
        }
    ],
    [
        CheckoutSteps.PAYMENT,
        {
            header: true,
            order: false,
            addMemberships: false,
            payment: true,
            invoiceResume: true,
            footer: true,
            dropIn: false,
            pos: false,
            done: false,
            payByLink: false
        }
    ],
    [
        CheckoutSteps.DROP_IN,
        {
            header: true,
            order: false,
            addMemberships: false,
            payment: false,
            invoiceResume: true,
            footer: false,
            dropIn: true,
            pos: false,
            done: false,
            payByLink: false
        }
    ],
    [
        CheckoutSteps.POS,
        {
            header: true,
            order: false,
            addMemberships: false,
            payment: false,
            invoiceResume: true,
            footer: false,
            dropIn: false,
            pos: true,
            done: false,
            payByLink: false
        }
    ],
    [
        CheckoutSteps.PAY_BY_LINK,
        {
            header: false,
            order: false,
            addMemberships: false,
            payment: false,
            invoiceResume: false,
            footer: false,
            dropIn: false,
            pos: false,
            done: false,
            payByLink: true
        }
    ],
    [
        CheckoutSteps.DONE,
        {
            header: false,
            order: false,
            addMemberships: false,
            payment: false,
            invoiceResume: false,
            footer: false,
            dropIn: false,
            pos: false,
            done: true,
            payByLink: false
        }
    ]
]);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%'
        },
        horizontalPaddings: {
            paddingLeft: 32,
            paddingRight: 32,
            [theme.breakpoints.down('sm')]: {
                paddingLeft: 16,
                paddingRight: 16
            }
        },
        headerContainer: {
            paddingTop: 37,
            paddingBottom: 18,
            [theme.breakpoints.down('sm')]: {
                paddingTop: 24,
                paddingBottom: 12
            }
        },
        bodyContainer: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height: '100%',
            paddingBottom: 37,
            [theme.breakpoints.down('sm')]: {
                paddingBottom: 24
            }
        },
        bodyBottomSpace: {
            height: 1,
            border: '1px solid white',
            marginTop: 37,
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        },
        footerContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 24,
            paddingBottom: 24,
            borderTop: '1px solid #D4D4D4',
            [theme.breakpoints.down('sm')]: {
                paddingTop: 6,
                paddingBottom: 12
            }
        },
        orderSpinnerContainer: {
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            paddingTop: '30%'
        },
        button: {
            minWidth: '100%'
        },
        section: {
            marginTop: 37,
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        },
        error: {
            color: '#C14A4A',
            width: '100%',
            fontSize: '16px',
            marginBottom: '10px'
        },
        alert: {
            marginBottom: 37,
            marginLeft: 32,
            marginRight: 32,
            [theme.breakpoints.down('sm')]: {
                marginBottom: 24,
                marginLeft: 16,
                marginRight: 16
            }
        },
        membershipButtons: {
            marginTop: 24
        },
        invoiceResume: {
            marginBottom: 24
        },
        refreshModalMembership: {
            'zIndex': 999999,
            '&': {
                zIndex: '999999 !important'
            }
        }
    })
);

const getStepTitle = (step: CheckoutSteps) => {
    switch (step) {
        case CheckoutSteps.ORDER:
            return 'Checkout';
        case CheckoutSteps.ADD_MEMBERSHIPS:
            return 'Add Membership';
        case CheckoutSteps.PAYMENT:
            return 'Payment Method';
        case CheckoutSteps.DROP_IN:
            return 'Credit Card';
        case CheckoutSteps.POS:
            return 'POS';
        default:
            return '';
    }
};

const validate = (paymentInfo: PaymentInfo): Array<string> => {
    const errors: Array<string> = [];

    if (paymentInfo.sendInvoice && isEmpty((paymentInfo.email || '').trim())) {
        errors.push('Email is required');
    }
    if (
        paymentInfo.sendInvoice &&
        !isEmpty((paymentInfo.email || '').trim()) &&
        !isEmailValid(paymentInfo.email || '')
    ) {
        errors.push('Email is invalid.');
    }

    return errors;
};

export const Checkout: FunctionComponent<CheckoutProps> = props => {
    const classes = useStyles(props);

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const order = useSelector<RootState, Order | undefined>(state => state.orders.order);
    const outdated = useSelector<
        RootState,
        { order: boolean; invoice?: boolean; invoiceFullPaid?: boolean } | undefined
    >(state => state.orders.outdated);
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);

    const invoice = useSelector<RootState, Invoice | undefined>(state => state.invoices.invoice);
    const invoicesStatus = useSelector<RootState, InvoicesStatus>(state => state.invoices.status);

    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const paymentsEnabled = useSelector<RootState, boolean | undefined>(
        state => state.payments.enrollmentStatus?.enabled
    );
    const createdPaymentLink = useSelector<RootState, PaymentLink | null>(state => state.payments.paymentLink);

    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const orderMemberships = useSelector<RootState, { orderId: number; hasMemberships: boolean } | null>(
        state => state.memberships.orderMemberships
    );

    const isSingleSale = props.type === 'singlesale';
    const isBooking = props.type === 'booking';
    const isMembership = props.type === 'membership';

    const [hasMembershipsToApply, setHasMembershipsToApply] = useState(false);
    const hasMembershipsApplied = useMemo(
        () => order !== undefined && getAllOrderItems(order).some(orderItem => orderItem.redeem !== undefined),
        [order]
    );

    const [show, setShow] = useState(
        isBooking ? showByStep.get(CheckoutSteps.ORDER)! : showByStep.get(CheckoutSteps.PAYMENT)!
    );

    const [loading, setLoading] = useState({
        order: true,
        invoice: true,
        enrollmentStatus: true,
        memberships: true,
        tip: false,
        payment: false,
        outdated: false
    });

    const [showModal, setShowModal] = useState({ discount: false, refresh: false });
    const [checkoutStep, setCheckoutStep] = useState(isBooking ? CheckoutSteps.ORDER : CheckoutSteps.PAYMENT);
    const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | undefined>();
    const [errors, setErrors] = useState<Array<string>>([]);
    const [buttonLabel, setButtonLabel] = useState('Pay');
    const [payLink, setPayLink] = useState('');

    const showClientWithoutPhoneAlert =
        paymentInfo?.paymentMethodId === PaymentMethodIds.PayByLink && isEmpty(invoice?.customer?.phone);

    const buttonDisabled =
        loading.order ||
        loading.invoice ||
        loading.enrollmentStatus ||
        loading.tip ||
        loading.outdated ||
        showClientWithoutPhoneAlert ||
        (checkoutStep === CheckoutSteps.PAYMENT &&
            (paymentInfo === undefined || paymentInfo.paymentMethodId === null) &&
            invoice &&
            Number(invoice.totalDue) > 0);

    useEffect(() => {
        dispatch(getEnrollmentStatusThunk(apiClientWrapper));
        amplitude.track(AMPLITUDE.LOAD_PAYMENT_METHOD_CHECK_OUT);

        if (props.orderId) {
            dispatch(getOrderThunk(apiClientWrapper, props.orderId));
            dispatch(checkOrderThunk(apiClientWrapper, props.orderId));
        } else if (props.bookingId) {
            props.bookingId && dispatch(migrateBookingThunk(apiClientWrapper, props.bookingId));
        } else {
            props.onClose();
        }
    }, []);

    useEffect(() => {
        if (invoice && Number(invoice.balance) <= 0 && checkoutStep === CheckoutSteps.PAYMENT) {
            setButtonLabel('Complete');
        } else if (paymentInfo?.paymentMethodId === PaymentMethodIds.PayByLink) {
            setButtonLabel('Send Link');
        } else {
            setButtonLabel('Pay');
        }
    }, [invoice, paymentInfo, checkoutStep]);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.GetSuccess:
                setLoading(prev => ({ ...prev, order: false }));
                if (order?.paid) {
                    setShowModal(prev => ({ ...prev, refresh: true }));
                } else {
                    order && dispatch(createInvoiceThunk(apiClientWrapper, order.id, props.bookingId));
                }
                break;
            case OrdersStatus.ReloadSuccess:
                setLoading(prev => ({ ...prev, order: false }));
                break;
            case OrdersStatus.MigrateSuccess:
                order && dispatch(createInvoiceThunk(apiClientWrapper, order.id, props.bookingId));
                order && dispatch(checkOrderThunk(apiClientWrapper, order.id));
                setLoading(prev => ({ ...prev, order: false }));
                break;
            case OrdersStatus.VerifyOutdatedSuccess:
                setLoading(prev => ({ ...prev, outdated: false }));
                if (outdated?.order || outdated?.invoice || outdated?.invoiceFullPaid) {
                    setShowModal(prev => ({ ...prev, refresh: true }));
                } else {
                    payHandler();
                }
                break;
            case OrdersStatus.Error:
                setLoading(prev => ({ ...prev, order: false, outdated: false }));
                break;
        }
    }, [ordersStatus]);

    useNonInitialEffect(() => {
        switch (invoicesStatus) {
            case InvoicesStatus.CreateInvoiceSuccess:
            case InvoicesStatus.Error:
                setLoading(prev => ({ ...prev, invoice: false }));
                break;
        }
    }, [invoicesStatus]);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.CheckOrderSuccess:
                setHasMembershipsToApply(
                    orderMemberships !== null &&
                        (orderMemberships.orderId === props.orderId || orderMemberships.orderId === order?.id) &&
                        orderMemberships.hasMemberships
                );
                setLoading(prev => ({ ...prev, memberships: false }));
                break;
            case MembershipsStatus.CheckOrderError:
                setHasMembershipsToApply(false);
                setLoading(prev => ({ ...prev, memberships: false }));
                break;
        }
    }, [membershipsStatus]);

    useNonInitialEffect(() => {
        switch (paymentsStatus) {
            case PaymentsStatus.GetEnrollmentStatusSuccess:
                setLoading(prev => ({ ...prev, enrollmentStatus: false }));
                break;
            case PaymentsStatus.SendSuccess:
                if (!isSingleSale) {
                    setCheckoutStep(CheckoutSteps.DONE);
                    setShow(showByStep.get(CheckoutSteps.DONE)!);
                    setLoading(prev => ({ ...prev, payment: false }));
                }
                props.onPaid && props.onPaid();
                break;
            case PaymentsStatus.CreatePaymentLinkSuccess:
                if (createdPaymentLink) {
                    const payLinkUrlPath = replace(PayByLinkPath, ':uuid', createdPaymentLink.uuid);
                    const fullPayLinkUrl = `${process.env.REACT_APP_BASE_URL}${payLinkUrlPath}`;
                    setPayLink(fullPayLinkUrl);

                    setCheckoutStep(CheckoutSteps.PAY_BY_LINK);
                    setShow(showByStep.get(CheckoutSteps.PAY_BY_LINK)!);
                    setLoading(prev => ({ ...prev, payment: false }));
                    props.onPayByLinkCreated && props.onPayByLinkCreated();
                }
                break;
            case PaymentsStatus.Error:
                setLoading(prev => ({ ...prev, enrollmentStatus: false, payment: false }));
        }
    }, [paymentsStatus]);

    useNonInitialEffect(() => {
        if (!props.hide) {
            setLoading(prev => ({ ...prev, order: true, invoice: true }));
            dispatch(createInvoiceThunk(apiClientWrapper, order?.id ?? 0, props.bookingId));
            dispatch(reloadOrderThunk(apiClientWrapper));
        }
    }, [props.hide]);

    /*const addDiscountHandler = () => {
        setShowModal(prev => ({...prev, discount: true}));
    };*/

    const clickPayButtonHandler = () => {
        setLoading(prev => ({ ...prev, outdated: true }));
        order?.id &&
            invoice?.id &&
            dispatch(verifyOutdatedThunk(apiClientWrapper, order.id, order.version, invoice.id, invoice.version));
    };

    const payHandler = () => {
        if (checkoutStep === CheckoutSteps.ORDER) {
            setCheckoutStep(CheckoutSteps.PAYMENT);
            setShow(showByStep.get(CheckoutSteps.PAYMENT)!);
        } else if (checkoutStep === CheckoutSteps.PAYMENT) {
            const errors = validate(paymentInfo!);
            setErrors(errors);
            if (errors.length === 0) {
                props.onStartPayment && props.onStartPayment();
                if (Number(invoice?.balance) > 0) {
                    switch (paymentInfo?.paymentMethodId) {
                        case PaymentMethodIds.Cash:
                        case PaymentMethodIds.Other:
                            setLoading(prev => ({ ...prev, payment: true }));
                            invoice?.id &&
                                paymentInfo.paymentMethodId &&
                                dispatch(
                                    confirmAndSendThunk(
                                        apiClientWrapper,
                                        invoice.id,
                                        paymentInfo.paymentMethodId,
                                        paymentInfo.sendInvoice,
                                        false,
                                        paymentInfo.email ?? ''
                                    )
                                );
                            break;
                        case PaymentMethodIds.CreditCard:
                            setCheckoutStep(CheckoutSteps.DROP_IN);
                            setShow(showByStep.get(CheckoutSteps.DROP_IN)!);
                            break;
                        case PaymentMethodIds.POS:
                            setCheckoutStep(CheckoutSteps.POS);
                            setShow(showByStep.get(CheckoutSteps.POS)!);
                            break;
                        case PaymentMethodIds.PayByLink:
                            setLoading(prev => ({ ...prev, payment: true }));
                            invoice?.id && dispatch(createPaymentLinkThunk(apiClientWrapper, invoice.id));
                            break;
                    }
                } else {
                    setLoading(prev => ({ ...prev, payment: true }));
                    dispatch(
                        invoice?.id &&
                            confirmAndSendThunk(
                                apiClientWrapper,
                                invoice.id,
                                PaymentMethodIds.Other,
                                paymentInfo?.sendInvoice ?? false,
                                false,
                                paymentInfo?.email ?? ''
                            )
                    );
                }
            }
        }
    };

    const backHandler = () => {
        switch (checkoutStep) {
            case CheckoutSteps.ORDER:
                props.onBack && props.onBack();
                break;
            case CheckoutSteps.ADD_MEMBERSHIPS:
                setCheckoutStep(CheckoutSteps.ORDER);
                setShow(showByStep.get(CheckoutSteps.ORDER)!);
                break;
            case CheckoutSteps.PAYMENT:
                if (isBooking) {
                    setCheckoutStep(CheckoutSteps.ORDER);
                    setShow(showByStep.get(CheckoutSteps.ORDER)!);
                    setPaymentInfo(undefined);
                } else if (props.onBack) {
                    props.onBack();
                }
                break;
            case CheckoutSteps.DROP_IN:
            case CheckoutSteps.POS:
                setCheckoutStep(CheckoutSteps.PAYMENT);
                setShow(showByStep.get(CheckoutSteps.PAYMENT)!);
                break;
        }
    };

    const applyMembershipsHandler = () => {
        setCheckoutStep(CheckoutSteps.ADD_MEMBERSHIPS);
        setShow(showByStep.get(CheckoutSteps.ADD_MEMBERSHIPS)!);
    };

    const changePaymentInfoHandler = (paymentInfo: PaymentInfo) => {
        setPaymentInfo(paymentInfo);
    };

    const successCreditCardPaymentHandler = (creditCardSessionId: string) => {
        setLoading(prev => ({ ...prev, payment: true }));
        invoice &&
            paymentInfo?.paymentMethodId === PaymentMethodIds.CreditCard &&
            dispatch(
                confirmAndSendThunk(
                    apiClientWrapper,
                    invoice.id!,
                    paymentInfo.paymentMethodId,
                    paymentInfo.sendInvoice,
                    false,
                    paymentInfo.email ?? '',
                    creditCardSessionId
                )
            );
    };

    const successPosPaymentHandler = () => {
        setLoading(prev => ({ ...prev, payment: true }));
        invoice &&
            paymentInfo?.paymentMethodId === PaymentMethodIds.POS &&
            dispatch(sendThunk(apiClientWrapper, invoice.id!, paymentInfo.sendInvoice, false, paymentInfo.email ?? ''));
    };

    const addPhoneNumberHandler = () => {
        props.onEditClient && invoice?.customer?.id && props.onEditClient(invoice?.customer?.id);
    };

    const refreshHandler = () => {
        if (props.onBack) {
            props.onBack();
        } else {
            props.onClose();
        }
    };

    return (
        <>
            <SidePanel
                side="right"
                showCloseButton={true}
                onClose={props.onClose}
                zIndex={isMembership ? 99999 : undefined}
                hide={props.hide}
            >
                <Box className={classes.container}>
                    {show.done && (
                        <PaymentDone
                            date={moment()}
                            total={Number(invoice?.balance ?? 0) > 0 ? invoice?.balance ?? '' : '0'}
                            type={props.type}
                            onClose={props.onClose}
                        />
                    )}
                    {show.header && (
                        <Box className={clsx(classes.headerContainer, classes.horizontalPaddings)}>
                            <CheckoutHeader
                                title={getStepTitle(checkoutStep)}
                                loading={loading.order}
                                onBack={
                                    props.onBack || (checkoutStep !== CheckoutSteps.ORDER && isBooking)
                                        ? backHandler
                                        : undefined
                                }
                                onClose={props.onClose}
                                onAddDiscount={/*addDiscountHandler*/ undefined}
                            />
                        </Box>
                    )}
                    {!show.addMemberships && (
                        <OverlayScrollbarsComponent style={{ height: '100%' }}>
                            <Box className={clsx(classes.bodyContainer, classes.horizontalPaddings)}>
                                {show.order && !loading.order && order !== undefined && (
                                    <CheckoutOrder
                                        order={order}
                                        matchMemebrships={hasMembershipsToApply || hasMembershipsApplied}
                                    />
                                )}
                                {show.order &&
                                    !loading.order &&
                                    order !== undefined &&
                                    (hasMembershipsToApply || hasMembershipsApplied) && (
                                        <MembershipsButtons
                                            order={order}
                                            className={classes.membershipButtons}
                                            onClickButton={applyMembershipsHandler}
                                        />
                                    )}
                                {show.payment &&
                                    !loading.invoice &&
                                    !loading.enrollmentStatus &&
                                    !loading.order &&
                                    invoice !== undefined && (
                                        <CheckoutPayment
                                            invoice={invoice}
                                            initPaymentMethodId={paymentInfo?.paymentMethodId}
                                            paymentsEnabled={paymentsEnabled ?? false}
                                            paymentMethods={props.paymentMethods}
                                            hideTips={props.hideTips}
                                            disabled={loading.payment}
                                            onSavingTip={saving => setLoading(prev => ({ ...prev, tip: saving }))}
                                            onChangePaymentInfo={changePaymentInfoHandler}
                                        />
                                    )}
                                {(show.order || show.payment) && loading.order && (
                                    <Box className={classes.orderSpinnerContainer}>
                                        <Spinner />
                                    </Box>
                                )}
                                {show.dropIn && invoice && (
                                    <CreditCardPayment
                                        invoiceId={invoice.id!}
                                        onSuccess={successCreditCardPaymentHandler}
                                        onBack={backHandler}
                                    />
                                )}
                                {show.pos && invoice && (
                                    <PosPayment
                                        invoiceId={invoice.id!}
                                        className={classes.section}
                                        hideTitle={true}
                                        onSuccess={successPosPaymentHandler}
                                        onGetPosServiceId={(posTerminalId, posServiceId) =>
                                            setPaymentInfo(prev => ({
                                                ...prev!,
                                                posTerminalId,
                                                posServiceId
                                            }))
                                        }
                                    />
                                )}
                                {show.payByLink && invoice && (
                                    <PayByLink
                                        payLink={payLink}
                                        totalBalance={invoice?.totalDue}
                                        onClose={() => props.onPayByLinkSent && props.onPayByLinkSent()}
                                    />
                                )}
                                {!loading.order && order && <Box className={classes.bodyBottomSpace} />}
                            </Box>
                        </OverlayScrollbarsComponent>
                    )}
                    {showClientWithoutPhoneAlert && !loading.order && !loading.invoice && (
                        <NoPhoneAlert
                            className={classes.alert}
                            onAddPhoneNumber={addPhoneNumberHandler}
                        />
                    )}
                    {show.footer && (
                        <Box className={clsx(classes.footerContainer, classes.horizontalPaddings)}>
                            {show.invoiceResume && (
                                <InvoiceResume
                                    invoice={invoice}
                                    loadingInvoice={loading.invoice}
                                    loadingTip={loading.tip}
                                    className={classes.invoiceResume}
                                />
                            )}
                            {errors.map((error, index) => (
                                <Typography
                                    className={classes.error}
                                    key={index}
                                >
                                    {error}
                                </Typography>
                            ))}
                            <Button
                                color="black"
                                label={buttonLabel}
                                className={classes.button}
                                disabled={buttonDisabled}
                                loading={loading.payment || loading.outdated}
                                onClick={clickPayButtonHandler}
                            />
                        </Box>
                    )}
                    {show.addMemberships && !loading.order && order !== undefined && (
                        <Memberships
                            order={order}
                            bookingId={props.bookingId}
                            onClose={backHandler}
                        />
                    )}
                </Box>
            </SidePanel>
            {showModal.discount && (
                <DiscountModal
                    total={295}
                    onClose={() => setShowModal(prev => ({ ...prev, discount: false }))}
                />
            )}
            {showModal.refresh && (
                <RefreshModal
                    refreshButtonLabel="Refresh Checkout"
                    onRefresh={refreshHandler}
                    onClose={() => setShowModal(prev => ({ ...prev, refresh: false }))}
                    className={isMembership ? classes.refreshModalMembership : undefined}
                />
            )}
        </>
    );
};

export default Checkout;
